import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import { Avatar, Button, Empty, Loading } from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import { EDUCATION_LEVELS } from '../../constants';

const levelLabel = (v) => (EDUCATION_LEVELS.find((l) => l.value === v) || {}).label || 'Other';

const Section = ({ title, children }) => (
  <div className="card sh-sm" style={{ marginBottom: 16 }}>
    <div className="t-title" style={{ marginBottom: 10 }}>{title}</div>
    {children}
  </div>
);

const FileLink = ({ label, value }) => (
  <a className="link-btn" href={fileUrl(value)} target="_blank" rel="noreferrer" style={{ display: 'block', padding: '4px 0' }}>
    📎 {label}
  </a>
);

/** One person's work profile, as an employer sees it. */
const SeekerDetailPage = () => {
  const { id } = useParams();
  const [person, setPerson] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.user.seeker(id).then((r) => setPerson(r.data)).catch((e) => setError(e.message));
  }, [id]);

  if (error) return <Empty emoji="🔍" title="Profile not available" text={error} />;
  if (!person) return <Loading />;

  const place = [person.city, person.district, person.state].filter(Boolean).join(', ');

  return (
    <div>
      <PageHeader title={person.name || 'Work profile'} subtitle={person.jobTitle || 'Open to work'} />

      <Reveal>
        <div className="card sh-sm" style={{ marginBottom: 16, textAlign: 'center' }}>
          <Avatar name={person.name} uri={person.avatar} size={88} />
          <div className="h3" style={{ marginTop: 10 }}>{person.name || 'KAAMWALA member'}</div>
          <div className="t-small">{person.jobTitle || 'Open to work'}</div>
          <div className="row wrap" style={{ gap: 6, marginTop: 10, justifyContent: 'center' }}>
            <span className="pill">{person.experienceYears ? `${person.experienceYears} yr experience` : 'Fresher'}</span>
            {place ? <span className="pill">📍 {place}</span> : null}
          </div>
          <div style={{ marginTop: 14 }}>
            <Button
              title={`Call ${person.name ? person.name.split(' ')[0] : 'them'}`}
              icon="📞"
              onClick={() => { window.location.href = `tel:${person.phone}`; }}
            />
          </div>
        </div>

        {(person.skills || []).length ? (
          <Section title="Skills"><div className="t-body">{person.skills.join(' · ')}</div></Section>
        ) : null}

        {person.resume ? (
          <Section title="Resume"><FileLink label="Open resume" value={person.resume} /></Section>
        ) : null}

        {(person.experienceLetters || []).length ? (
          <Section title="Experience letters">
            {person.experienceLetters.map((f, i) => <FileLink key={f} label={`Letter ${i + 1}`} value={f} />)}
          </Section>
        ) : null}

        {(person.education || []).length ? (
          <Section title="Education">
            {person.education.map((e, i) => (
              <div key={`${e.level}-${i}`} className="row-line">
                <div className="t-title">{levelLabel(e.level)}{e.course ? ` · ${e.course}` : ''}</div>
                <div className="t-tiny">
                  {[e.institute, e.passingYear ? `Passed ${e.passingYear}` : ''].filter(Boolean).join(' · ') || 'No details'}
                </div>
                {e.proof ? <FileLink label="Certificate" value={e.proof} /> : null}
              </div>
            ))}
          </Section>
        ) : null}

        {(person.workAnswers || []).length ? (
          <Section title="More about them">
            {person.workAnswers.map((a) => (
              <div key={a.key} className="row-line">
                <div className="t-tiny">{a.label}</div>
                {a.kind === 'file'
                  ? (a.files || []).map((f, i) => <FileLink key={f} label={`File ${i + 1}`} value={f} />)
                  : <div className="t-body">{a.value || '—'}</div>}
              </div>
            ))}
          </Section>
        ) : null}
      </Reveal>
    </div>
  );
};

export default SeekerDetailPage;
